import * as React from 'react';
import type { WeatherPayload, WeatherInfo, MultidayForecast } from '../schema';
import getWeatherForLocation from '../utils/getWeatherForLocation';
import { lazyGetWeatherTemperatureUnit } from 'owa-weather-option';
import { usePatchPayload } from 'owa-timestream-asset-sdk';

export default function useWeatherViewState({ selectedLocation }: WeatherPayload) {
    const [locationName, setLocationName] = React.useState<string>('');
    const [weatherInfo, setWeatherInfo] = React.useState<WeatherInfo | undefined>();
    const [multidayForecast, setMultidayForecast] = React.useState<MultidayForecast | undefined>();
    const [attribution, setAttribution] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [showRetryError, setShowRetryError] = React.useState<boolean>(false);
    const getWeatherTemperatureUnit = lazyGetWeatherTemperatureUnit.tryImportForRender();
    const weatherUnit = getWeatherTemperatureUnit?.();
    const patchPayload = usePatchPayload<WeatherPayload>();

    const fetchWeatherInfo = React.useCallback(() => {
        setIsLoading(true);
        getWeatherForLocation(selectedLocation!, weatherUnit!).then(response => {
            setIsLoading(false);

            if (response?.weatherDataForLocations && response.weatherDataForLocations.length > 0) {
                const weatherLocation = response.weatherDataForLocations[0]!;
                const conditions = weatherLocation.conditions;
                const multidayConditions = weatherLocation.multidayForecast?.dailyForecasts;
                const attribution = conditions?.attribution;

                setLocationName(weatherLocation.locationId?.Name ?? '');
                if (conditions?.temperature) {
                    setWeatherInfo({
                        temp: conditions.temperature,
                        skyCode: Number(conditions.skyCode),
                        skyText: String(conditions.skyText),
                        tempUnit: weatherUnit,
                    });
                    patchPayload({
                        selectedLocation,
                        temp: conditions.temperature,
                        skyCode: Number(conditions.skyCode),
                        skyText: String(conditions.skyText),
                        tempUnit: weatherUnit,
                    });
                }

                if (multidayConditions) {
                    setMultidayForecast({
                        forecast: multidayConditions.map(condition => ({
                            temp: Number(condition?.high),
                            high: Number(condition?.high),
                            low: Number(condition?.low),
                            skyText: String(condition?.skyText),
                        })),
                    });
                }

                if (attribution?.text) {
                    setAttribution(attribution.text);
                }
            } else {
                setShowRetryError(true);
                return;
            }
        });
    }, [selectedLocation?.text, weatherUnit]);

    React.useEffect(() => {
        if (!!selectedLocation?.text && weatherUnit != undefined) {
            fetchWeatherInfo();
        } else {
            setLocationName('');
            setWeatherInfo(undefined);
            setMultidayForecast(undefined);
            setShowRetryError(false);
        }
    }, [selectedLocation?.text, weatherUnit]);

    return {
        locationName: locationName,
        locationPersona: selectedLocation,
        weatherInfo: weatherInfo,
        multidayForecast: multidayForecast,
        attribution: attribution,
        fetchWeatherCallback: fetchWeatherInfo,
        isLoading: isLoading,
        showRetryError,
    };
}
